import React, { useEffect, useMemo, useState } from "react";
import {
    SovosBox,
    SovosButton,
    SovosDialog,
    SovosDialogActions,
    SovosDialogContent,
    SovosDialogTitle,
    SovosIconButton,
    SovosMenuItem,
    SovosSelect,
    SovosTextField,
    SovosTypography,
} from "mosaic-react";
import { Close } from "mosaic-react-icons";
import { STAGING_TRANSLATION_OPERATORS, operatorMeta } from "./featureFlags";

const parseArrayValue = (raw) => {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map(String);
    } catch (_e) {
        // fallthrough — old/comma-separated stored value
    }
    return String(raw).split(",").map((s) => s.trim()).filter(Boolean);
};

const TranslationModal = ({
    open,
    mode = "create",
    initialValues = null,
    columnOptions = [],
    onCancel,
    onSubmit,
    submitting = false,
    submitError = null,
}) => {
    const [name, setName] = useState("");
    const [columnName, setColumnName] = useState("");
    const [operator, setOperator] = useState("eq");
    const [findValueText, setFindValueText] = useState("");
    const [findValueChips, setFindValueChips] = useState([]);
    const [chipDraft, setChipDraft] = useState("");
    const [replaceValue, setReplaceValue] = useState("");
    const [touched, setTouched] = useState(false);

    useEffect(() => {
        if (!open) return;
        setTouched(false);
        if (initialValues) {
            setName(initialValues.name || "");
            setColumnName(initialValues.column_name || "");
            const op = initialValues.operator || "eq";
            setOperator(op);
            const meta = operatorMeta(op);
            if (meta?.findValueShape === "array") {
                setFindValueChips(parseArrayValue(initialValues.find_value));
                setFindValueText("");
            } else if (meta?.findValueShape === "string") {
                setFindValueText(initialValues.find_value || "");
                setFindValueChips([]);
            } else {
                setFindValueText("");
                setFindValueChips([]);
            }
            setReplaceValue(initialValues.replace_value || "");
        } else {
            setName("");
            setColumnName(columnOptions[0] || "");
            setOperator("eq");
            setFindValueText("");
            setFindValueChips([]);
            setReplaceValue("");
        }
        setChipDraft("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialValues]);

    const meta = operatorMeta(operator);
    const showsFindValue = meta?.hasFindValue;
    const isChipMode = meta?.findValueShape === "array";

    const isValid = useMemo(() => {
        if (!name.trim()) return false;
        if (!columnName) return false;
        if (!operator) return false;
        if (replaceValue == null) return false;
        if (showsFindValue) {
            if (isChipMode) {
                if (findValueChips.length === 0) return false;
            } else if (!findValueText && findValueText !== "0") {
                return false;
            }
        }
        return true;
    }, [name, columnName, operator, replaceValue, showsFindValue, isChipMode, findValueChips, findValueText]);

    const handleSubmit = () => {
        setTouched(true);
        if (!isValid) return;
        let findValue = null;
        if (showsFindValue) {
            findValue = isChipMode ? JSON.stringify(findValueChips) : findValueText;
        }
        onSubmit({
            name: name.trim(),
            columnName,
            operator,
            findValue,
            replaceValue,
        });
    };

    const handleChipCommit = () => {
        const value = chipDraft.trim();
        if (!value) return;
        if (findValueChips.includes(value)) {
            setChipDraft("");
            return;
        }
        setFindValueChips((prev) => [...prev, value]);
        setChipDraft("");
    };

    const handleRemoveChip = (chip) => {
        setFindValueChips((prev) => prev.filter((c) => c !== chip));
    };

    if (!open) return null;

    return (
        <SovosDialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <SovosDialogTitle>
                {mode === "edit" ? "Edit Translation" : "New Translation"}
                <SovosIconButton
                    onClick={onCancel}
                    tooltipText="Close"
                    sx={{ position: "absolute", right: 8, top: 8 }}
                >
                    <Close />
                </SovosIconButton>
            </SovosDialogTitle>
            <SovosDialogContent>
                <SovosTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Matching cells are replaced with the new value in full. Pattern-based
                    transforms (e.g. extract digits from "INV-1007") will be available with the
                    AI assistant in a future release.
                </SovosTypography>

                {mode === "edit" && (
                    <SovosBox sx={{
                        p: 1,
                        mb: 2,
                        borderRadius: 1,
                        bgcolor: "warning.light",
                        border: "1px solid",
                        borderColor: "warning.main",
                    }}>
                        <SovosTypography variant="caption" sx={{ color: "warning.dark", fontWeight: 600 }}>
                            Saving does not modify rows already updated by the previous version.
                            Click Apply Now after saving to re-run with the new rule.
                        </SovosTypography>
                    </SovosBox>
                )}

                <SovosTextField
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. PT to Portugal"
                    fullWidth
                    required
                    error={touched && !name.trim()}
                    sx={{ mb: 2 }}
                />

                <SovosBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <SovosBox>
                        <SovosTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                            Column*
                        </SovosTypography>
                        <SovosSelect
                            value={columnName || ""}
                            onChange={(e) => setColumnName(e.target.value)}
                            fullWidth
                            displayEmpty
                        >
                            <SovosMenuItem value="" disabled>Select a column</SovosMenuItem>
                            {columnOptions.map((col) => (
                                <SovosMenuItem key={col} value={col}>{col}</SovosMenuItem>
                            ))}
                        </SovosSelect>
                    </SovosBox>

                    <SovosBox>
                        <SovosTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                            Operator*
                        </SovosTypography>
                        <SovosSelect
                            value={operator}
                            onChange={(e) => setOperator(e.target.value)}
                            fullWidth
                        >
                            {STAGING_TRANSLATION_OPERATORS.map((op) => (
                                <SovosMenuItem key={op.value} value={op.value}>{op.label}</SovosMenuItem>
                            ))}
                        </SovosSelect>
                    </SovosBox>

                    {operator === "is_not_null" && (
                        <SovosBox sx={{
                            p: 1,
                            borderRadius: 1,
                            bgcolor: "warning.light",
                            border: "1px solid",
                            borderColor: "warning.main",
                        }}>
                            <SovosTypography variant="caption" sx={{ color: "warning.dark", fontWeight: 600 }}>
                                This will match every non-empty cell in the column.
                            </SovosTypography>
                        </SovosBox>
                    )}

                    {showsFindValue && !isChipMode && (
                        <SovosTextField
                            label="Find value*"
                            value={findValueText}
                            onChange={(e) => setFindValueText(e.target.value)}
                            placeholder="Value to match"
                            fullWidth
                            required
                            error={touched && !findValueText}
                        />
                    )}

                    {showsFindValue && isChipMode && (
                        <SovosBox>
                            <SovosTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                                Find values* (press Enter to add)
                            </SovosTypography>
                            <SovosTextField
                                value={chipDraft}
                                onChange={(e) => setChipDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === ",") {
                                        e.preventDefault();
                                        handleChipCommit();
                                    }
                                }}
                                onBlur={handleChipCommit}
                                placeholder='e.g. "PT" then Enter'
                                fullWidth
                                error={touched && findValueChips.length === 0}
                            />
                            <SovosBox sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {findValueChips.map((chip) => (
                                    <SovosBox
                                        key={chip}
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: 2,
                                            bgcolor: "primary.light",
                                        }}
                                    >
                                        <SovosTypography variant="caption" sx={{ fontWeight: 600 }}>
                                            {chip}
                                        </SovosTypography>
                                        <SovosIconButton
                                            onClick={() => handleRemoveChip(chip)}
                                            tooltipText="Remove"
                                            size="small"
                                        >
                                            <Close sx={{ fontSize: 14 }} />
                                        </SovosIconButton>
                                    </SovosBox>
                                ))}
                            </SovosBox>
                        </SovosBox>
                    )}

                    <SovosTextField
                        label="Replace with*"
                        value={replaceValue}
                        onChange={(e) => setReplaceValue(e.target.value)}
                        placeholder="New value"
                        fullWidth
                        required
                        error={touched && replaceValue == null}
                    />
                </SovosBox>

                {submitError && (
                    <SovosTypography variant="caption" sx={{ color: "error.main", mt: 1, display: "block" }}>
                        {submitError}
                    </SovosTypography>
                )}
            </SovosDialogContent>
            <SovosDialogActions>
                <SovosButton onClick={onCancel} variant="text">Cancel</SovosButton>
                <SovosButton
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting || !isValid}
                >
                    {submitting ? "Saving..." : mode === "edit" ? "Save changes" : "Create translation"}
                </SovosButton>
            </SovosDialogActions>
        </SovosDialog>
    );
};

export default TranslationModal;
